import { Button, Colors, Modal, Text } from '@pinchenterprisesnpm/friday-ui'
import RichTextEditor from '@src/ui/components/common/RichTextEditor'
import { InfoBanner } from '@src/features/shift-post-v2/components/info-banner/info-banner'
import { CustomAsyncSelect } from '@src/features/shift-post-v2/steps/shift-details-step/components/location-section/components/custom-async-select'
import { FormatOptionLabel } from '@src/features/shift-post-v2/steps/shift-details-step/components/location-section/components/format-option-label/format-option-label'
import { useEditArrivalInstructions } from '@src/features/shift-post-v2/steps/shift-details-step/components/location-section/hooks/useEditArrivalInstructions'
import {
  ILocationSectionFormValues,
  TLocationDropdownOption,
} from '@src/features/shift-post-v2/steps/shift-details-step/components/location-section/location-section.types'
import {
  FooterWrapper as Footer,
  StableScrollableModalContent as Content,
} from 'ui/components/common/style'
import { debouncedLoadLocationOptions } from '@src/features/shift-post-v2/steps/shift-details-step/components/location-section/location-section.utils'
import { connect, FormikProps } from 'formik'
import { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { selectBusinessEntity } from '@src/data/BusinessEntity/selectors'
import { USER_ROLE } from '@src/models/User'
import AuthService from '@src/services/AuthService'

const { Header } = Modal

interface IEditArrivalInstructionsModalProps {
  isOpen: boolean
  closeModal: () => void
  isConfirmStep?: boolean
  formik?: FormikProps<ILocationSectionFormValues>
  handleLocationChange: (
    location: TLocationDropdownOption,
    customArrivalInstructions?: string
  ) => void
}

export const EditArrivalInstructionsModal = connect<IEditArrivalInstructionsModalProps>(
  ({ isOpen, closeModal, formik, isConfirmStep, handleLocationChange }) => {
    const {
      values: { location, property_id, keyForLocationsDropdown },
      errors,
    } = formik

    const businessEntity = useSelector(selectBusinessEntity)

    const [modalSelectedLocation, setModalSelectedLocation] =
      useState<TLocationDropdownOption>(location)

    // Create a wrapper function that includes the business_id
    const loadLocationOptionsWithBusinessId = (inputValue: string, callback: any) => {
      const currentUserRole = AuthService.getLoggedInUserRole()
      const isSuperAdmin =
        currentUserRole === USER_ROLE.ACCOUNT_MANAGER_LEVEL_1 ||
        currentUserRole === USER_ROLE.ACCOUNT_MANAGER_LEVEL_2 ||
        currentUserRole === USER_ROLE.ADMIN

      // For Super Admin, AM1 and AM2 users, don't send businessEntity.id (they should see all locations)
      if (isSuperAdmin) {
        debouncedLoadLocationOptions(inputValue, callback)
      } else {
        // Only make the API call if businessEntity is loaded
        if (businessEntity?.id) {
          debouncedLoadLocationOptions(inputValue, callback, businessEntity.id)
        } else {
          // If businessEntity is not loaded yet, return empty options
          callback([])
        }
      }
    }

    const {
      arrivalInstructions,
      arrivalInstructionsErrors,
      setArrivalInstructions,
      handleEditArrivalInstructions,
      isSubmitting,
    } = useEditArrivalInstructions(
      formik,
      closeModal,
      modalSelectedLocation,
      handleLocationChange,
      isConfirmStep,
      location,
      property_id
    )

    const handleDropdownLocationChange = (location: TLocationDropdownOption) => {
      setModalSelectedLocation(location)
      setArrivalInstructions(location?.property_arrival_instructions)
    }

    const handleCancel = () => {
      closeModal()
      setArrivalInstructions(location?.property_arrival_instructions)
    }

    const description = `Editing Arrival Instructions here will edit them for all shifts posted at ${location?.name}.\nAre you sure you want to edit?`

    const title = useMemo(() => {
      return isConfirmStep ? 'Edit Location' : 'Edit Arrival Instructions'
    }, [isConfirmStep])

    return (
      <Modal isOpen={isOpen} closeModal={handleCancel} width='800px' height='max-content'>
        <Header title={title} />

        <Content>
          {isConfirmStep && (
            <>
              <Text my='md' color={Colors.dark}>
                You can edit location and the arival instructions from here.
              </Text>

              {businessEntity?.id ? (
                <CustomAsyncSelect
                  label='Location'
                  menuPlacement='bottom'
                  value={modalSelectedLocation}
                  placeholder='Please select a location...'
                  isOutlined
                  rounded={true}
                  size='large'
                  loadOptions={loadLocationOptionsWithBusinessId}
                  defaultOptions={true}
                  my='sm'
                  onChange={handleDropdownLocationChange}
                  key={keyForLocationsDropdown}
                  errorMessage={errors.property_id && !property_id ? errors.property_id : undefined}
                  formatOptionLabel={(value: TLocationDropdownOption) => (
                    <FormatOptionLabel value={value} />
                  )}
                  ptValueContainer='16px'
                />
              ) : (
                <div style={{ padding: '16px', textAlign: 'center', color: '#666' }}>
                  Loading locations...
                </div>
              )}
            </>
          )}

          {!isConfirmStep && <InfoBanner description={description} />}

          <Text my='sm'>Arrival Instructions</Text>

          <RichTextEditor
            placeholder='Arrival Instructions *'
            name='property_arrival_instructions'
            value={arrivalInstructions}
            handleChange={(_, value) => setArrivalInstructions(value)}
            handleBlur={() => {}}
            errorMessage={arrivalInstructionsErrors}
            fieldTouched={true}
            borderRadius='10px'
            borderColor={Colors.border}
            minHeight='200px'
          />
        </Content>

        <Footer>
          <Button isFilledOutlined onClick={handleCancel}>
            Cancel
          </Button>

          <Button
            onClick={handleEditArrivalInstructions}
            isDisabled={isSubmitting || !!arrivalInstructionsErrors}
            isLoading={isSubmitting}
          >
            Save
          </Button>
        </Footer>
      </Modal>
    )
  }
)
